/**
 * Integrytas Group - Base App (mobile)
 * Builds the mobile navigation tree from the flat, RBAC-filtered PageAccess[].
 *
 * Mirrors buildMenuTree() in TabManagerShell so the mobile drawer shows exactly
 * the same hierarchy/ordering the desktop menu does — driven entirely by the
 * user's live entitlement (can_view + parent_page_id + sort_order).
 */

import type { PageAccess } from '../types/auth';
import { is_nav_group_code } from '../lib/nav_groups';

export interface MobileMenuNode {
  page: PageAccess;
  label: string;
  children: MobileMenuNode[];
  /**
   * Effective desktop-only state: this page's own `desktop_only` flag OR any
   * ancestor's (the flag cascades down the menu). When true, tapping the item
   * opens the "Desktop Version Required" modal instead of routing.
   */
  desktopOnly: boolean;
}

/** Builds a tree from the flat PageAccess[]. Orphans are promoted to roots. */
export function buildMobileMenuTree(
  menu: PageAccess[],
  pageLabels: Record<string, string>,
): MobileMenuNode[] {
  const visible = menu.filter((p) => p.can_view);
  const byId = new Map<number, MobileMenuNode>();
  for (const p of visible) {
    byId.set(p.page_id, {
      page: p,
      label: pageLabels[p.page_internal_code] ?? p.page_internal_code,
      children: [],
      desktopOnly: false,
    });
  }
  const roots: MobileMenuNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.page.parent_page_id;
    const parent = parentId != null ? byId.get(parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortFn = (a: MobileMenuNode, b: MobileMenuNode) =>
    a.page.sort_order - b.page.sort_order || a.label.localeCompare(b.label);
  const sortRecursive = (nodes: MobileMenuNode[]) => {
    nodes.sort(sortFn);
    for (const n of nodes) sortRecursive(n.children);
  };
  sortRecursive(roots);
  // Cascade desktop_only down the tree: a node is effectively desktop-only when
  // its own flag OR any ancestor's flag is set (set the indicator on a parent
  // NAV group and it filters to every sub-option).
  const applyCascade = (nodes: MobileMenuNode[], inherited: boolean) => {
    for (const n of nodes) {
      const effective = inherited || n.page.desktop_only;
      n.desktopOnly = effective;
      applyCascade(n.children, effective);
    }
  };
  applyCascade(roots, false);
  return roots;
}

/** A node is openable if it renders a real page (leaf, not a pure nav group). */
export function isOpenable(node: MobileMenuNode): boolean {
  return node.children.length === 0 && !is_nav_group_code(node.page.page_internal_code);
}

/** Flattens all openable leaves — used for the search overlay. */
export function flattenOpenableLeaves(nodes: MobileMenuNode[]): MobileMenuNode[] {
  const out: MobileMenuNode[] = [];
  const walk = (list: MobileMenuNode[]) => {
    for (const n of list) {
      if (isOpenable(n)) out.push(n);
      walk(n.children);
    }
  };
  walk(nodes);
  return out;
}
